import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

const EMPTY = {
  name: "", contact_person: "", phone: "", email: "", address: "",
  registration_id: "", registration_type: "", registration_authority: "",
  compliance_contact_name: "", compliance_contact_number: "",
  emergency_contact_name: "", emergency_contact_number: "",
  partner_share_percent: 50, effective_from: "", settlement_terms: "", active: true, notes: "",
};
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const digits = (s) => String(s || "").replace(/\D/g, "");

const validatePartner = (f) => {
  const e = {};
  const req = {
    name: "Partner organisation / legal name", contact_person: "Primary contact person",
    phone: "Primary contact number", email: "Official email", address: "Address",
    registration_id: "Government registration/license identifier",
    registration_type: "Registration / license type", registration_authority: "Registration authority",
    compliance_contact_name: "Authorised compliance contact",
    emergency_contact_name: "Emergency contact name", emergency_contact_number: "Emergency contact number",
    effective_from: "Active / effective date",
  };
  Object.entries(req).forEach(([k, l]) => { if (!String(f[k] || "").trim()) e[k] = `${l} is required.`; });
  if (f.email && !EMAIL_RE.test(f.email.trim())) e.email = "Enter a valid official email address.";
  if (f.phone && digits(f.phone).length < 10) e.phone = "Enter a valid number (10+ digits).";
  if (f.emergency_contact_number && digits(f.emergency_contact_number).length < 10) e.emergency_contact_number = "Enter a valid number (10+ digits).";
  return e;
};

export default function LabPartners() {
  const { can } = useAuth();
  const manage = can("lab.partners.manage");
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [errs, setErrs] = useState({});
  const [formError, setFormError] = useState("");
  const [settle, setSettle] = useState(null);

  const load = useCallback(async () => {
    setItems(null);
    try { const { data } = await api.get("/lab/partners"); setItems(data.items); }
    catch (e) { setError(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setErrs({}); setFormError(""); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p, partner_share_percent: p.partner_share_percent ?? 50 }); setErrs({}); setFormError(""); setOpen(true); };
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    const v = validatePartner(form); setErrs(v);
    if (Object.keys(v).length) { setFormError(Object.values(v)[0]); return; }
    setFormError(""); setBusy(true);
    try {
      const payload = { ...form, partner_share_percent: Number(form.partner_share_percent) };
      if (editing) await api.patch(`/lab/partners/${editing.partner_id}`, payload);
      else await api.post("/lab/partners", payload);
      toast.success(editing ? "Partner updated." : "Partner added.");
      setOpen(false); load();
    } catch (e) { setFormError(apiError(e)); } finally { setBusy(false); }
  };

  const openSettlement = async (p) => {
    try { const { data } = await api.get(`/lab/partners/${p.partner_id}/settlement`); setSettle(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!can("lab.orders.view")) return <NoAccess what="Lab partners" />;
  const Err = ({ k }) => errs[k] ? <p className="mt-1.5 text-xs font-medium text-red-600">{errs[k]}</p> : null;
  const T = ({ label, k, req, type = "text", full }) => (
    <Field label={`${label}${req ? " *" : ""}`} className={full ? "sm:col-span-2" : ""}>
      <input type={type} className={inputCls} data-testid={`partner-${k}`} value={form[k]} onChange={(e) => set(k)(e.target.value)} />
      <Err k={k} />
    </Field>
  );

  return (
    <div data-testid="lab-partners-page">
      <PageHeader overline="Laboratory" title="B2B Diagnostic Partners"
        subtitle="External diagnostic labs with their own compliance details, revenue-share arrangement and settlement statements."
        actions={manage && <button onClick={openNew} className={primaryBtnCls} data-testid="add-partner-btn"><Plus className="h-4 w-4" /> Add partner</button>} />

      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <Table testid="lab-partners-table" columns={["Partner", "Contact", "Registration", "Partner share", "Status", ""]}
          rows={items} empty={<EmptyState title="No partners yet" subtitle="Add a diagnostic partner and configure their revenue share." testid="lab-partners-empty" />}
          renderRow={(p) => (
            <tr key={p.partner_id} data-testid={`lab-partner-row-${p.partner_id}`}>
              <Td className="font-medium text-slate-900">{p.name}<span className="block text-xs text-slate-400">{p.email || "—"}</span></Td>
              <Td className="text-slate-600 text-sm">{p.contact_person || "—"}<span className="block text-xs text-slate-400">{p.phone}</span></Td>
              <Td className="text-slate-600 text-sm">{p.registration_id || "—"}<span className="block text-xs text-slate-400">{p.registration_authority || ""}</span></Td>
              <Td className="text-slate-700 font-medium">{p.partner_share_percent}% <span className="text-xs text-slate-400">/ hospital {100 - p.partner_share_percent}%</span></Td>
              <Td><span className={`text-xs font-medium ${p.active ? "text-emerald-600" : "text-slate-400"}`}>{p.active ? "Active" : "Inactive"}</span></Td>
              <Td>
                <div className="flex gap-2">
                  {can("lab.reports.view") && <button onClick={() => openSettlement(p)} className={ghostBtnCls} data-testid={`settle-partner-${p.partner_id}`}>Settlement</button>}
                  {manage && <button onClick={() => openEdit(p)} className={ghostBtnCls} data-testid={`edit-partner-${p.partner_id}`}>Edit</button>}
                </div>
              </Td>
            </tr>
          )} />
      )}

      <Modal open={open} onClose={() => setOpen(false)} wide title={editing ? "Edit partner" : "Add partner"}
        subtitle="Fields marked * are mandatory and validated on the server." testid="lab-partner-modal">
        <form onSubmit={save} className="space-y-5">
          <p className="overline text-[#EA580C]">Organisation & contact</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <T label="Partner organisation / legal name" k="name" req full />
            <T label="Primary contact person" k="contact_person" req />
            <T label="Primary contact number" k="phone" req />
            <T label="Official email" k="email" req />
            <div />
            <T label="Address" k="address" req full />
          </div>
          <p className="overline text-[#EA580C]">Government registration & compliance</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <T label="Registration / license identifier" k="registration_id" req />
            <T label="Registration / license type" k="registration_type" req />
            <T label="Issuing authority" k="registration_authority" req />
            <div />
            <T label="Authorised compliance / governance contact" k="compliance_contact_name" req />
            <T label="Compliance contact number" k="compliance_contact_number" />
          </div>
          <p className="overline text-[#EA580C]">Emergency contact</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <T label="Emergency contact name" k="emergency_contact_name" req />
            <T label="Emergency contact number" k="emergency_contact_number" req />
          </div>
          <p className="overline text-[#EA580C]">Revenue share & settlement</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <T label="Partner share (%)" k="partner_share_percent" type="number" />
            <T label="Active / effective from" k="effective_from" type="date" req />
            <T label="Settlement terms" k="settlement_terms" full />
          </div>
          {formError && <p className="text-sm text-red-600" data-testid="partner-form-error">{formError}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setOpen(false)} className={ghostBtnCls}>Cancel</button>
            <button disabled={busy} className={primaryBtnCls} data-testid="save-partner-btn">{busy ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </Modal>

      {settle && (
        <Modal open onClose={() => setSettle(null)} wide title={`Settlement — ${settle.partner.name}`} subtitle={`Partner share ${settle.share_percent}%`} testid="lab-settlement-modal">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-sm">
            <div className="panel p-3"><p className="text-xs text-slate-400">Orders</p><p className="font-semibold">{settle.summary.orders}</p></div>
            <div className="panel p-3"><p className="text-xs text-slate-400">Gross (₹)</p><p className="font-semibold">{settle.summary.gross}</p></div>
            <div className="panel p-3"><p className="text-xs text-slate-400">Partner share (₹)</p><p className="font-semibold text-[#EA580C]">{settle.summary.partner_share}</p></div>
            <div className="panel p-3"><p className="text-xs text-slate-400">Hospital share (₹)</p><p className="font-semibold text-emerald-600">{settle.summary.hospital_share}</p></div>
          </div>
          <Table testid="settlement-table" columns={["Order", "Date", "Patient", "Total", "Partner", "Hospital"]}
            rows={settle.rows} empty={<EmptyState title="No partner orders in range" testid="settlement-empty" />}
            renderRow={(r) => (
              <tr key={r.order_no}>
                <Td className="font-mono text-xs">{r.order_no}</Td><Td className="text-xs">{r.date}</Td>
                <Td>{r.patient_name}</Td><Td>₹{r.total}</Td>
                <Td className="text-[#EA580C]">₹{r.partner_share}</Td><Td className="text-emerald-600">₹{r.hospital_share}</Td>
              </tr>
            )} />
        </Modal>
      )}
    </div>
  );
}
